import { Component, OnInit, Input} from '@angular/core';
//import { GridOptions, ColDef, GridApi } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { Subscription, Observable, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { IssuesService } from "../../../../common/services/issues.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { ComplianceReport, ComplianceReportSearchSpec, ComplianceReportStatus, ComplianceReportingType } from "../../../../issues/models/compliance.model";
import { TabComponent } from '../../../../common/components/tabs/tab.component';

import { RouterLinkRendererComponent } from "../../../../common/components/ag-grid/router-link-renderer.component";
import { AuthService } from "../../../../services/auth.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { PendingFlow } from '../../../../flow/models/flow.model';
import { Organization, OrganizationStub } from '../../../../common/models/organization.model';
import { ApplicationState, ApplicationType, MetadataSearchSpec } from '../../../../applications/models/applications.models';
import { OrganizationService } from '../../../../common/services/organization.service';
import { ApplicationsService } from '../../../../applications/services/applications.service';
import { FlowIndex } from '../../../../common/models/flow.dto';
import { ModalInfoComponent } from '../../../../common/components/ag-grid/modal-info.component';
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';


@Component({
    selector: 'compliance-reports-external-view',
    templateUrl: './compliance-reports-external-view.component.html',
    styleUrls: ['./compliance-reports-external-view.component.scss'],
    standalone: false
})
export class ComplianceReportsExternalViewComponent implements OnInit {
    @Input() widgetTitle: string;

    approvedReports: PendingFlow[];
    incompleteReports: PendingFlow[];
    returnedReports: PendingFlow[];
    pendingReports: PendingFlow[];
    organizations: OrganizationStub[];
    types: ComplianceReportingType[] = [];


	approvedGridOptions: GridOptions;
	approvedGridApi?: GridApi;
	returnedGridOptions: GridOptions;
	returnedGridApi?: GridApi;
	pendingGridOptions: GridOptions;
	pendingGridApi?: GridApi;
	incompleteGridOptions: GridOptions;
	incompleteGridApi?: GridApi;

    apiNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "API #",
        headerTooltip: "API #",
        field: "aPINumber",
        tooltipField: "aPINumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150

    } as ColDef;

    applicationNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: "applicationNumber",
            tooltipField: "applicationNumber",
            openInNewWindow: false,
            url: (data: PendingFlow) => { return `/issues/${data.applicationNumber}/review` }
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 160
    } as ColDef;

    incompleteApplicationNumber: ColDef = {
        headerClass: "wrapped",
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        cellRendererSelector: (params) => {
            if (params.data.onBehalf) {
                return {
                    component: ModalInfoComponent,
                    params: {
                        modalText: (data) => { return `Application ${data.applicationNumber} is a paper submission that must be completed by ODNR.`; },
                        text: (data) => { return data.applicationNumber; }
                    }
                }
            }
            else {
               
               return {
                    component: RouterLinkRendererComponent,
                    params: {
                        textField: "applicationNumber",
                        text: params.data.applicationNumber,
                        targetUrl: (data: PendingFlow) => { return `/issues/${data.applicationNumber}/resume` },
                        url: (data: PendingFlow) => { return `/issues/${data.applicationNumber}/resume` }
                    }
                }
            }
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 160
    } as ColDef;

    approvedDateColumn: ColDef = {
        comparator: dateComparator,
        headerClass: "wrapped",
        headerName: "Approved Date",
        headerTooltip: "Approved Date",
        field: "approvedDate",
        tooltipField: "approvedDate",
        valueGetter: this.formatter.formatDate("approvedDate"),
        tooltipValueGetter: this.formatter.formatDate("approvedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        maxWidth: 130,
        minWidth: 130
    } as ColDef;

    pendingApplicationNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 160
    } as ColDef;

    approvedApplicationNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 140,
        maxWidth: 140
    } as ColDef;

    returnedApplicationNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "applicationNumber",
        tooltipField: "applicationNumber",
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: "applicationNumber",
            tooltipField: "applicationNumber",
            openInNewWindow: false,
            url: (data: PendingFlow) => { return `/issues/${data.applicationNumber}/resume` }
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 130

    } as ColDef;

    organizationColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Organization",
        headerTooltip: "Organization",
        field: "companyName",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        maxWidth: 200,
        minWidth: 150
    } as ColDef;

    reportTypeColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Type of Report",
        headerTooltip: "Type of Report",
        field: "reportType",
        valueGetter: this.formatReportType("reportType"),
        tooltipValueGetter: this.formatReportType("reportType"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 200
    } as ColDef;

    submittedColumn: ColDef = {
        comparator: dateComparator,
        headerClass: "wrapped",
        headerName: "Submitted Date",
        headerTooltip: "Submitted Date",
        field: "createdDate",
        tooltipField: "createdDate",
        valueGetter: this.formatter.formatDate("createdDate"),
        tooltipValueGetter: this.formatter.formatDate("createdDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        maxWidth: 130,
        minWidth: 130
    } as ColDef;

    wellNameColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Name",
        headerTooltip: "Well Name",
        field: "wellName",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        valueFormatter: this.formatter.formatPotentiallyBlankField("wellName", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("wellName", "N/A"),
        maxWidth: 150,
        minWidth: 125,
        resizable: true
    } as ColDef;

    wellNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Number",
        headerTooltip: "Well Number",
        field: "wellNumber",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        valueFormatter: this.formatter.formatPotentiallyBlankField("wellNumber", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("wellNumber", "N/A"),
        maxWidth: 125,
        minWidth: 125
    } as ColDef;

    dueDateColumn: ColDef = {
        comparator: dateComparator,
        headerClass: "wrapped",
        headerName: "Due Date",
        headerTooltip: "Due Date",
        field: "dueDate",
        tooltipField: "dueDate",
        valueGetter: this.formatter.formatDate("dueDate"),
        tooltipValueGetter: this.formatter.formatDate("dueDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        maxWidth: 100,
        minWidth: 100
    } as ColDef;


    approvedColumns: ColDef[] = [
        this.submittedColumn,
        this.approvedDateColumn,
        this.approvedApplicationNumberColumn,
        this.organizationColumn,
        this.apiNumberColumn,
        this.wellNameColumn,
        this.wellNumberColumn,
        this.reportTypeColumn,
        
    ];

    returnedColumns: ColDef[] = [
        this.submittedColumn,
        this.returnedApplicationNumberColumn,
        this.organizationColumn,
        this.apiNumberColumn,
        this.wellNameColumn,
        this.wellNumberColumn,
        this.reportTypeColumn
    ];

    pendingColumns: ColDef[] = [
        this.submittedColumn,
        this.pendingApplicationNumberColumn,
        this.organizationColumn,
        this.apiNumberColumn,
        this.wellNameColumn,
        this.wellNumberColumn,
        this.reportTypeColumn
    ];

    incompleteColumns: ColDef[] = [
        this.incompleteApplicationNumber,
        this.organizationColumn,
        this.apiNumberColumn,
        this.wellNameColumn,
        this.wellNumberColumn,
        this.reportTypeColumn
    ]
 

    subscription: Subscription = new Subscription();

    constructor(private issuesService: IssuesService, private formatter: GridFormatterService, private organizationService: OrganizationService, public appService: ApplicationsService, public auth: AuthService) {


    }

    ngOnInit() {

        let type$: Observable<ComplianceReportingType[]> = (this.types == null || this.types.length == 0) ? this.issuesService.getComplianceReportingTypes().pipe(tap((types: ComplianceReportingType[]) => { this.types = types; })) : of(this.types);

        let tSubs$ = type$.pipe().subscribe()
        this.subscription.add(tSubs$);

        this.loadData();
		this.pendingGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.pendingColumns, false);
		this.pendingGridOptions.onGridReady = (params) => { this.pendingGridApi = params.api; };
        this.pendingGridOptions.headerHeight = 50;
		this.returnedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.returnedColumns, false);
		this.returnedGridOptions.onGridReady = (params) => { this.returnedGridApi = params.api; };
        this.returnedGridOptions.headerHeight = 50;
		this.approvedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.approvedColumns, false);
		this.approvedGridOptions.onGridReady = (params) => { this.approvedGridApi = params.api; };
        this.approvedGridOptions.headerHeight = 50;
		this.incompleteGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.incompleteColumns, false);
		this.incompleteGridOptions.onGridReady = (params) => { this.incompleteGridApi = params.api; };
        this.incompleteGridOptions.headerHeight = 50;
   
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    formatOrganization(field: string): any {
        return (obj) => {
            let returnValue = "N/A";
            if (obj && obj.data && obj.data[field] != null) {
                returnValue = obj.data[field];
                if (this.organizations != null) {
                    let orgs = this.organizations.filter(o => o.id == returnValue);
                    if (orgs == null || orgs.length === 0) orgs = this.organizations.filter(o => o.historicalOwnerNumber === returnValue);
                    if (orgs != null) {
                        const registered = orgs.filter(o => o.hasRegistered === true);
                        if (registered != null && registered.length > 0) {
                            returnValue = registered[0].name;
                        } else {
                            returnValue = orgs[0].name;
                        }
                    }
                }
            }
            return returnValue;
        }
    }

    formatReportType(field: string): any {
        return (obj) => {
            let returnValue = "N/A";
            if (obj && obj.data && obj.data[field] != null) {
                returnValue = obj.data[field];
                if (this.types != null) {
                    const type = this.types.find(o => o.typeId === returnValue);
                    if (type != null) returnValue = type.description;
                }
            }
            return returnValue;
        }
    }

   

    loadData(): void {

        var spec = {
            applicationTypes: [ApplicationType.ComplianceReporting],
            applicationStates: [],
            organizationId: this.auth.User.organizationId
        } as MetadataSearchSpec;

        let obs$ = this.appService.searchMetadata(spec).pipe(
            map((meta: FlowIndex[]) => {
                return meta
            }),
            catchError((error) => {
                console.error(error);
                return of([]);
            })

        )


        let subs$ = obs$.pipe().subscribe((meta: FlowIndex[]) => {

            this.approvedReports = meta.filter(f => f.state == ApplicationState.Approved);
            this.returnedReports = meta.filter(f => f.state == ApplicationState.Corrections)
            this.pendingReports = meta.filter(f => f.state == ApplicationState.InitialReview)
            this.incompleteReports = meta.filter(f => f.state == ApplicationState.Input && f.onBehalf == false);

        })

        this.subscription.add(subs$);


    }

    

    onTabClicked(tab: TabComponent) {
        let api: GridApi = null;
        switch (tab.title) {
            case "Returned For Corrections":
				if (this.returnedGridApi != null) api = this.returnedGridApi;
                if (this.returnedReports == null) this.loadData();
                break;
           
            case "Accepted":
                if (this.approvedGridApi != null) api = this.approvedGridApi;
                if (this.approvedReports == null) this.loadData();
                break;
            case "Pending":

                if (this.pendingGridApi != null) api = this.pendingGridApi;
                if (this.pendingReports == null) this.loadData();
                break;
            case "Incomplete":
                if (this.incompleteGridApi != null) api = this.incompleteGridApi;
                if (this.incompleteReports == null) this.loadData();
                break;
        }
        if (api != null) api.sizeColumnsToFit();
    }

}
